import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { ChevronRight, ChevronDown, Network } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api } from "../../lib/api";
import { Kpi, Th, Td, SectionTitle, Loading, usd, num } from "./shared";

const NS_COLORS = ["#00579e", "#15803d", "#6d28d9", "#b45309", "#be185d", "#0e7490", "#94a3b8"];

const csv = (rows, name) => {
  const head = Object.keys(rows[0] || {}).join(",");
  const body = rows.map((r) => Object.values(r).map((v) => `"${v}"`).join(",")).join("\n");
  const blob = new Blob([`${head}\n${body}`], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
};

export const KubeCosts = ({ clusterId }) => {
  const [data, setData] = useState(null);
  const [groupBy, setGroupBy] = useState("namespace");
  const [open, setOpen] = useState({});
  const [netOpen, setNetOpen] = useState(null);

  useEffect(() => {
    setData(null);
    api.kube.costs(clusterId, groupBy).then(setData).catch(console.error);
  }, [clusterId, groupBy]);

  if (!data) return <Loading label="Loading cost analysis…" testId="kube-costs-loading" />;
  const t = data.totals;
  const dimLabel = data.dimensions.find((d) => d.id === groupBy)?.label || "Namespace";

  return (
    <div className="space-y-6" data-testid="kube-costs">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi testId="kube-cost-total" label="Cost / mo" value={usd(t.total_usd)} />
        <Kpi testId="kube-cost-compute" label="Compute" value={usd(t.compute_usd)} />
        <Kpi testId="kube-cost-storage" label="Storage" value={usd(t.storage_usd)} />
        <Kpi testId="kube-cost-network" label="Network" value={usd(t.network_usd)} sub={`${t.network_pct}% of cost · ${num(t.network_gb / 1024, 1)} TB`} accent="text-[#be185d]" />
        <Kpi testId="kube-cost-platform" label="Platform overhead" value={usd(t.platform_overhead_usd)} sub={`${t.platform_overhead_pct}% · ASTRA agents ${usd(t.astra_agent_usd)}`} />
        <Kpi testId="kube-cost-headroom" label="Headroom" value={usd(t.headroom_usd)} sub="warm spare capacity" />
      </div>

      <div className="cs-card p-5" data-testid="kube-cost-trend">
        <SectionTitle hint="Headroom is drawn as its own series so spare capacity is never hidden inside a group.">
          Daily cost by {dimLabel.toLowerCase()} · last 8 days
        </SectionTitle>
        <ResponsiveContainer width="100%" height={210}>
          <BarChart data={data.daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 4" vertical={false} stroke="hsl(var(--border))" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
            <Tooltip formatter={(v) => usd(v, false)} contentStyle={{ fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {[...data.top_namespaces, "Headroom"].map((ns, i) => (
              <Bar key={ns} dataKey={ns} stackId="a" fill={NS_COLORS[i % NS_COLORS.length]} radius={i === data.top_namespaces.length ? [2, 2, 0, 0] : 0} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="cs-card p-5" data-testid="kube-cost-tree">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <SectionTitle hint={`Drill from ${dimLabel.toLowerCase()} to workload; every row splits compute, storage and network.`}>Cost list</SectionTitle>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Group by</span>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="h-8 w-[190px] text-xs" data-testid="kube-groupby-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {data.dimensions.map((d) => (
                  <SelectItem key={d.id} value={d.id} data-testid={`kube-groupby-${d.id}`}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button data-testid="kube-export-costs"
              onClick={() => csv(data.namespaces.map(({ workloads, ...r }) => r), `kube-costs-${clusterId}.csv`)}
              className="px-3 py-1.5 rounded-sm border border-border text-xs hover:bg-[hsl(var(--cs-tint))] transition-colors">
              Export CSV
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr><Th>{dimLabel} / workload</Th><Th>Kind</Th><Th right>Share</Th><Th right>Compute</Th><Th right>Storage</Th><Th right>Network</Th><Th right>Data</Th><Th right>Total</Th></tr>
            </thead>
            <tbody>
              {data.namespaces.map((n) => (
                <React.Fragment key={n.namespace}>
                  <tr className="border-t border-border bg-[hsl(var(--cs-tint))]/40 cursor-pointer"
                    onClick={() => setOpen((o) => ({ ...o, [n.namespace]: !o[n.namespace] }))}
                    data-testid={`kube-ns-row-${n.namespace}`}>
                    <Td className="font-medium">
                      <span className="inline-flex items-center gap-1">
                        {open[n.namespace] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        {n.namespace}
                        {n.platform_overhead && <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-white border border-border text-[9px] uppercase tracking-wide text-muted-foreground">platform</span>}
                      </span>
                    </Td>
                    <Td className="text-muted-foreground">{n.workloads.length} workload{n.workloads.length === 1 ? "" : "s"}</Td>
                    <Td right mono>{n.share_pct}%</Td>
                    <Td right mono>{usd(n.compute_usd)}</Td>
                    <Td right mono>{usd(n.storage_usd)}</Td>
                    <Td right mono className="text-[#be185d]">{usd(n.network_usd)}</Td>
                    <Td right mono className="text-muted-foreground">{num(n.network_gb / 1024, 1)} TB</Td>
                    <Td right mono className="font-semibold">{usd(n.total_usd)}</Td>
                  </tr>
                  {open[n.namespace] && n.workloads.map((w) => (
                    <tr key={w.workload} className="border-t border-border/60" data-testid={`kube-wl-row-${w.workload}`}>
                      <Td className="pl-8">{w.workload}</Td>
                      <Td className="text-muted-foreground">{w.kind} · {w.replicas}</Td>
                      <Td right mono>{w.share_pct}%</Td>
                      <Td right mono>{usd(w.compute_usd)}</Td>
                      <Td right mono>{usd(w.storage_usd)}</Td>
                      <Td right mono>
                        <button className="text-[#be185d] hover:underline inline-flex items-center gap-1"
                          data-testid={`kube-net-${w.workload}`} onClick={() => setNetOpen(w)}>
                          <Network className="w-3 h-3" />{usd(w.network_usd)}
                        </button>
                      </Td>
                      <Td right mono className="text-muted-foreground">{num(w.network_gb / 1024, 1)} TB</Td>
                      <Td right mono className="font-semibold">{usd(w.total_usd)}</Td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {netOpen && (
          <div className="mt-4 p-4 rounded-sm border border-border bg-[hsl(var(--cs-tint))]/50" data-testid="kube-net-breakdown">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold">{netOpen.namespace || ""}{netOpen.workload} · network cost by traffic type</div>
              <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setNetOpen(null)}>close</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2.5">
              {[["Inter-AZ (cross-zone)", "inter_az"], ["Intra-AZ", "intra_az"], ["Inter-Region", "inter_region"], ["Internet egress", "internet"]].map(([l, key]) => (
                <div key={key} className="bg-white rounded-sm border border-border px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{l}</div>
                  <div className="cs-mono text-sm font-semibold">{usd(netOpen.network_split[key].usd)}</div>
                  <div className="cs-mono text-[10px] text-muted-foreground">{num(netOpen.network_split[key].gb / 1024, 1)} TB</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="cs-card p-5" data-testid="kube-network-lever">
        <SectionTitle hint={data.network_lever.note}>
          Cross-zone network lever · {usd(data.network_lever.total_savings_usd)}/mo available
        </SectionTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr><Th>Workload</Th><Th>Namespace</Th><Th right>Inter-AZ cost</Th><Th right>Share of workload cost</Th><Th>Proposed action</Th><Th right>Est. savings</Th><Th>Risk</Th></tr></thead>
            <tbody>
              {data.network_lever.candidates.map((c) => (
                <tr key={c.workload} className="border-t border-border" data-testid={`kube-netlever-${c.workload}`}>
                  <Td className="font-medium">{c.workload}</Td>
                  <Td className="text-muted-foreground">{c.namespace}</Td>
                  <Td right mono className="text-[#be185d]">{usd(c.inter_az_usd)}</Td>
                  <Td right mono>{c.network_share_of_cost_pct}%</Td>
                  <Td className="max-w-[320px]">{c.proposed_action}<div className="text-[10px] text-muted-foreground mt-0.5">{c.evidence}</div></Td>
                  <Td right mono className="text-[#15803d] font-semibold">{usd(c.estimated_savings_usd)}</Td>
                  <Td className={c.risk === "low" ? "text-[#15803d]" : "text-[#92400e]"}>{c.risk}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default KubeCosts;
